import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  Fade,
  useTheme,
  alpha,
  CircularProgress,
  Chip,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Breadcrumb from 'components/Breadcrumb';
import { useAuth } from 'contexts/AuthContext';
import { logger } from 'utils/logger';
import {
  Participant,
  participantsService,
} from './services/ParticipantsService';
import RedactParticipantDialog from './components/RedactParticipantDialog';

const log = logger.getLogger('ParticipantsPage');

const REDACT_PRIVILEGE = 'REDACT_PARTICIPANT_GLOBAL';

const headerCellStyle = (theme: Theme) => ({
  fontWeight: 600,
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.025em',
  py: 1.5,
  borderBottom: `2px solid ${alpha(theme.palette.divider, 0.7)}`,
  backgroundColor: theme.palette.background.default,
});

const findEmail = (p: Participant): string => {
  if (p.clientId && p.clientId.includes('@')) return p.clientId;
  const emailContact = p.contacts?.find((c) => c.type === 'EMAIL');
  return emailContact?.value || p.clientId || '';
};

const findMobile = (p: Participant): string => {
  const mobile = p.contacts?.find((c) => c.type === 'MOBILE');
  return mobile?.value || '';
};

const formatDate = (date?: string): string => {
  if (!date) return '-';
  try {
    return new Date(date).toLocaleString();
  } catch {
    return date;
  }
};

const ParticipantsPage: React.FC = () => {
  const theme = useTheme();
  const { auth } = useAuth();

  const userPrivs: string[] = useMemo(
    () => (auth.user?.accountProfile?.privs as string[]) || [],
    [auth.user]
  );
  const canRedact = userPrivs.includes(REDACT_PRIVILEGE);

  const [searchInput, setSearchInput] = useState<string>('');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const [redactTarget, setRedactTarget] = useState<Participant | null>(null);

  const performSearch = useCallback(async () => {
    const term = searchInput.trim();
    if (!term) return;
    setLoading(true);
    setError(null);
    setHasSearched(true);
    try {
      const result = await participantsService.searchParticipants({
        search: term,
        page: 0,
        size: 50,
      });
      setParticipants(result.data);
    } catch (err: any) {
      log.error('Failed to search participants', err);
      setError('Failed to search participants. Please try again.');
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  }, [searchInput]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        performSearch();
      }
    },
    [performSearch]
  );

  const handleRedactClose = useCallback(() => {
    setRedactTarget(null);
    if (searchInput.trim()) {
      performSearch();
    }
  }, [performSearch, searchInput]);

  const tableContent = useMemo(() => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </TableCell>
        </TableRow>
      );
    }
    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={8} align="center" sx={{ color: 'error.main' }}>
            {error}
          </TableCell>
        </TableRow>
      );
    }
    if (!hasSearched) {
      return (
        <TableRow>
          <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
            Enter a name or email above and press Enter or click Search.
          </TableCell>
        </TableRow>
      );
    }
    if (participants.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
            No participants found.
          </TableCell>
        </TableRow>
      );
    }

    return participants.map((p) => (
      <TableRow key={p.id} hover>
        <TableCell sx={{ color: 'text.secondary' }}>{p.id}</TableCell>
        <TableCell sx={{ color: 'text.secondary' }}>{p.accountId ?? '-'}</TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {[p.first, p.middle, p.last].filter(Boolean).join(' ') || '-'}
          </Typography>
        </TableCell>
        <TableCell>{findEmail(p) || '-'}</TableCell>
        <TableCell>{findMobile(p) || '-'}</TableCell>
        <TableCell align="center">
          <Chip
            label={p.active ? 'Active' : 'Inactive'}
            color={p.active ? 'success' : 'default'}
            size="small"
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {formatDate(p.updateDate)}
          </Typography>
        </TableCell>
        <TableCell align="center">
          {canRedact ? (
            <Tooltip title="Redact Participant">
              <IconButton
                color="error"
                onClick={() => setRedactTarget(p)}
                sx={{
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <DeleteForeverIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="You do not have permission to redact participants">
              <span>
                <IconButton color="error" disabled>
                  <DeleteForeverIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    ));
  }, [loading, error, hasSearched, participants, canRedact, theme.palette.error.main]);

  return (
    <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
      <Breadcrumb
        items={[
          {
            label: 'Participants',
            icon: <PersonSearchIcon fontSize="small" sx={{ fontSize: '1rem' }} />,
          },
        ]}
      />

      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 3 }}
      >
        Participants
      </Typography>

      <Fade in={true} timeout={600}>
        <Paper
          elevation={2}
          sx={{
            width: '100%',
            mb: 3,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            p={2.5}
            display="flex"
            alignItems="center"
            gap={1.5}
            sx={{
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              background: theme.palette.background.default,
            }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Search by name or email"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 480 }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={performSearch}
              disabled={loading || !searchInput.trim()}
              startIcon={<SearchIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '12px',
                px: 2,
              }}
            >
              Search
            </Button>
          </Box>

          <TableContainer>
            <Table sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellStyle(theme)}>ID</TableCell>
                  <TableCell sx={headerCellStyle(theme)}>Account ID</TableCell>
                  <TableCell sx={headerCellStyle(theme)}>Name</TableCell>
                  <TableCell sx={headerCellStyle(theme)}>Email / Client ID</TableCell>
                  <TableCell sx={headerCellStyle(theme)}>Mobile</TableCell>
                  <TableCell align="center" sx={headerCellStyle(theme)}>
                    Status
                  </TableCell>
                  <TableCell sx={headerCellStyle(theme)}>Last Updated</TableCell>
                  <TableCell align="center" sx={headerCellStyle(theme)}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                sx={{
                  '& .MuiTableRow-root:nth-of-type(odd)': {
                    backgroundColor: alpha(theme.palette.background.default, 0.4),
                  },
                }}
              >
                {tableContent}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Fade>

      <RedactParticipantDialog
        open={!!redactTarget}
        participant={redactTarget}
        onClose={handleRedactClose}
      />
    </Container>
  );
};

export default ParticipantsPage;
